import React, { useState, useCallback } from "react";
import { BellIcon, ChevronDownIcon, Menu, SearchIcon } from "lucide-react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../components/ui/avatar";
import { Input } from "../../components/ui/input";
import { Separator } from "../../components/ui/separator";
import MainLogo from "../logo";
import { ProfilePopup } from "../../screens/ProfilePopup/ProfilePopup";
import { useSelector } from "react-redux";
import { RootState } from "store";
import debounce from "lodash/debounce";

interface Ipros{
  className?:string
}

export const Header: React.FC<Ipros> = ({className}) => {
  const [isProfilePopupOpen, setIsProfilePopupOpen] = useState(false);

  const toggleProfilePopup = () => {
    setIsProfilePopupOpen(!isProfilePopupOpen);
  };

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (window.location.pathname === '/my-predictions') {
        const event = new CustomEvent('searchPredictions', {
          detail: value
        });
        window.dispatchEvent(event);
      }
    }, 500),
    []
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length >= 3 || value.length === 0) {
      debouncedSearch(value);
    }
  };

  return (
    <header className={`z-[1] border-b border-[#dadce3] relative sm:static bg-custom-gradient shadow-custom flex h-[60px] w-full items-center justify-between border-b border-[#d8d8d8] sm:bg-none sm:bg-white px-6 py-2.5 ${className}`}>
      {/* Logo */}
      <div className="flex items-center gap-2.5 py-px">
        {/* <div className="sm:hidden">
          <Menu className="pt-1" data-testid="menu-icon" />
        </div> */}
        <MainLogo />
      </div>

      {/* User section */}
      <div className="flex items-center gap-4 sm:gap-6 pl-4">
        {/* <div className="flex items-center justify-end gap-[21px]">
          <BellIcon className="h-6 w-6" data-testid="bell-icon" />
        </div> */}

        <div className="h-[36.5px] hidden sm:block border-l border-[#d8d8d8]" />

        <div className="flex w-auto items-center justify-end gap-4">
          <div className="flex items-center justify-center gap-2.5 hidden sm:block">
            <div className="text-base font-bold leading-6 text-[#333333]">
              {useSelector((state: RootState) => {
                const fullName = state.user.fullName;
                return fullName && fullName.length > 10 ? (
                  <div title={fullName} className="text-ellipsis overflow-hidden w-[120px] whitespace-nowrap text-left">
                    {fullName}
                  </div>
                ) : fullName
              })}
            </div>
          </div>

          <div 
            className="cursor-pointer flex items-center gap-2.5" 
            onClick={toggleProfilePopup}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                toggleProfilePopup();
              }
            }}
            role="button"
            tabIndex={0}
            data-testid="avatar-container"
          >
            <Avatar className="h-10 w-10">
              {useSelector((state: RootState) => {
                const profileImage = state.user.profileImage;
                const fullName = state.user.fullName;
                if (profileImage) {
                  return (
                    <AvatarImage asChild>
                      <img
                        src={profileImage}
                        alt={fullName || ''}
                        className="object-cover w-full h-full"
                      />
                    </AvatarImage>
                  );
                }
                return (
                  <AvatarFallback asChild data-testid="avatar-fallback">
                    <div>
                      {(() => {
                        if (!fullName) return '';
                        const names = fullName.split(' ');
                        return names.length > 1 
                          ? (names[0][0] + names[1][0]).toUpperCase()
                          : names[0][0].toUpperCase();
                      })()}
                    </div>
                  </AvatarFallback>
                );
              })}
            </Avatar>
            <ChevronDownIcon className="h-6 w-6 hidden sm:block" />
          </div>
          {isProfilePopupOpen && (
            <div className="fixed z-[99] bg-[#444444BD] sm:bg-transparent backdrop-blur-[5px] sm:backdrop-blur-none h-full sm:h-auto sm:absolute top-0 sm:top-16 -left-px sm:left-auto bottom:0 sm:bottom:auto right-0 sm:right-6 z-10">
              <ProfilePopup onClose={() => setIsProfilePopupOpen(false)} />
            </div>
          )}
        </div>
      </div>
    </header>
  );
};